import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { plansApi } from "@/api/plans";
import { ApiError } from "@/api/client";
import { useState } from "react";

export const Route = createFileRoute("/app/projects/$pid/plan")({
  component: PlanPage,
});

function PlanPage() {
  const { pid } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: cur } = useQuery({ queryKey: ["project-plan", pid], queryFn: () => plansApi.forProject(pid) });
  const { data: plans } = useQuery({ queryKey: ["plans"], queryFn: plansApi.list });
  const [pending, setPending] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: (planId: string) => plansApi.requestChange(pid, planId, promoCode.trim() || undefined),
    onSuccess: (r) => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ["project-plan", pid] });
      // Comp (100%-off): the switch applied inline — no deposit needed.
      if (r.is_comp) {
        setMsg("Plan changed — applied at no charge.");
        return;
      }
      // Otherwise hand off to the deposit page to pay for the new cycle.
      void navigate({
        to: "/app/projects/$pid/pay/$paymentId",
        params: { pid, paymentId: r.payment_id },
      });
    },
    onError: (e) => {
      setMsg(null);
      setErr(e instanceof ApiError ? e.message : "Couldn't change plan");
    },
    onSettled: () => setPending(null),
  });

  return (
    <div className="space-y-8">
      <section className="card max-w-2xl">
        <h3 className="text-sm uppercase tracking-wide text-ink/55">Current plan</h3>
        <div className="mt-2 text-2xl font-semibold">{cur?.plan?.display_name ?? "—"}</div>
        <div className="mt-1 text-sm text-ink/65">
          {cur?.paid_until ? `Paid until ${new Date(cur.paid_until).toLocaleDateString()}` : "No active payment"}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-medium">Change plan</h2>
        <p className="mt-1 text-sm text-ink/65">
          Changing your plan starts a fresh billing cycle at the new plan's price. You'll be sent to a
          payment page to confirm. Have a promo code? Enter it below and it'll be applied to the charge.
        </p>

        <div className="mt-4 max-w-sm">
          <label className="label">Promo code <span className="text-ink/45">(optional)</span></label>
          <input
            className="input mt-1"
            placeholder="e.g. EARLY30"
            value={promoCode}
            onChange={(e) => { setPromoCode(e.target.value); setErr(null); }}
          />
        </div>

        {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
        {msg && <p className="mt-3 text-sm text-green-700">{msg}</p>}

        <ul className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {plans?.map((p) => {
            const isCurrent = p.id === cur?.plan?.id;
            return (
              <li key={p.id} className={`card ${isCurrent ? "border-primary-500 ring-1 ring-primary-500" : ""}`}>
                <div className="flex items-baseline justify-between">
                  <h3 className="text-lg font-semibold">{p.display_name}</h3>
                  <div className="text-right">
                    <div className="text-2xl font-semibold">${p.monthly_price_usd}</div>
                    <div className="text-xs text-ink/55">/ {p.cycle_days}d</div>
                  </div>
                </div>
                <ul className="mt-4 space-y-1 text-sm text-ink/65">
                  <li>{Number(p.monthly_credits).toLocaleString()} credits / cycle</li>
                  <li>{p.rps_limit} req/sec</li>
                  <li>{p.sse_connections_max} concurrent SSE</li>
                  <li>{p.max_tokens_per_sse_conn} tokens / SSE</li>
                </ul>
                <button
                  className={isCurrent ? "btn-secondary mt-6 w-full" : "btn-primary mt-6 w-full"}
                  disabled={isCurrent || pending === p.id}
                  onClick={() => { setErr(null); setMsg(null); setPending(p.id); change.mutate(p.id); }}
                >
                  {isCurrent ? "Current" : pending === p.id ? "Requesting…" : "Switch to this plan"}
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
